import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { filter, Subscription } from 'rxjs';
import { ApiResponseDocumentsService } from '../../services/api/response-documents/api-response-documents.service';
import { ApiResponseProjectsService } from '../../services/api/response-projects/api-response-projects.service';
import { DocumentProcessingStepEnum, isArchiveFile, ResponseProjectUploadFile } from '../../models/response-project-document';
import { ResponseProjetAiMessageScenario } from '../../models/response-project';
import {
  DocumentScope,
  ResponseDocumentSubtype,
  SCOPE_DCE,
  SCOPE_INTERNAL_DOC,
  SUBTYPE_OTHER_DISPLAYED,
  SUBTYPE_OTHER_NAME,
} from '../../models/response-document-subtype';
import { TendersResponsesService, WsConnection } from '../../services/tenders-responses.service';
import { MAX_ARCHIVE_SIZE_BYTES, MAX_FILE_SIZE_BYTES } from '../../services/document-import.constants';
import { DocumentImportContext } from '../../services/document-import.service';
import { ImportConflictSession } from '../../services/import-conflict-session';
import { ProjectDocumentImportService } from '../../services/project-document-import.service';
import { findUploadSubtype, uploadSubtypeDisplayName, uploadSubtypeSlug } from '../documents/document-types';
import { DedupSurface, DuplicateItem, DuplicateResolution } from '../documents/dedup/dedup.model';
import { RejectedFile } from '../../models/rejected-file';
import { UploaderChangeEvent } from '../../models/uploader-change-event';

export type { UploaderChangeEvent } from '../../models/uploader-change-event';

const MAX_DOCUMENTS_PER_SCOPE = 500;

@Component({
  selector: 'app-response-project-document-uploader',
  templateUrl: './response-project-document-uploader.component.html',
  styleUrls: ['./response-project-document-uploader.component.scss'],
})
export class ResponseProjectDocumentUploaderComponent implements OnChanges, AfterViewChecked, OnDestroy {
  readonly DocumentProcessingStepEnum = DocumentProcessingStepEnum;
  SUBTYPE_OTHER = SUBTYPE_OTHER_DISPLAYED;

  readonly dropzoneFormatHintKeys = [
    'tenders.responses.documents.hint-pdf',
    'tenders.responses.documents.hint-docx',
    'tenders.responses.documents.hint-xlsx',
    'tenders.responses.documents.hint-max-size',
  ];

  @Input() title = '';
  @Input() subtypeOptions: ResponseDocumentSubtype[] = [];
  @Input() mainTitle = '';
  @Input() scope!: DocumentScope;
  @Input() dedupSurface: DedupSurface = 'project';
  @Input() titleBadgeKind: 'required' | 'recommended' = 'required';
  @Input() dragFormatsText?: string;
  @Input() emptyStateSubhint = '';
  @Input() projectId: string | null = null;
  @Input() defaultDocumentSubtypeId?: number;
  @Input() featuredOption?: ResponseDocumentSubtype;
  @Input() set collapsed(value: boolean) {
    this.panelExpanded = !value;
  }

  @ViewChild('listContainer') listContainerRef?: ElementRef<HTMLElement>;

  @Input() configMode = false;
  @Input() contentDisabled = false;

  @Output() projectNameChange = new EventEmitter<string>();
  @Output() uploadStateChange = new EventEmitter<UploaderChangeEvent>();
  @Output() continueClicked = new EventEmitter<void>();
  @Output() projectNotFound = new EventEmitter<void>();
  @Output() documentNotFound = new EventEmitter<void>();

  isDragging = false;
  // Track drag depth so isDragging stays true while moving over child elements.
  // dragleave fires every time the pointer crosses into a child, so a simple
  // boolean toggle flickers; we only clear the state once all enters are balanced.
  private dragCounter = 0;
  ignoredFiles: RejectedFile[] = [];
  private scrollToFileIdPending: string | null = null;
  private scrollToTopPending = false;
  private tempFileCounter = 0;

  panelExpanded = true;

  accept = '.pdf,.docx,.xlsx,.doc,.xls,.rtf,.txt,.odt,.zip,.rar';

  private wsSubscriptions: Subscription[] = [];
  pendingProcess = false;
  isAddingToExisting = false;

  duplicates: DuplicateItem[] = [];
  private duplicateCounter = 0;
  private activeWsConn: WsConnection | null = null;
  private wsProcessingComplete = false;
  private readonly conflicts = new ImportConflictSession();

  constructor(
    private apiDocuments: ApiResponseDocumentsService,
    private apiProjects: ApiResponseProjectsService,
    private tendersResponses: TendersResponsesService,
    private projectImport: ProjectDocumentImportService
  ) {}

  private get importContext(): DocumentImportContext {
    return {
      projectId: this.projectId,
      scope: this.scope,
      defaultSubtypeId: this.defaultDocumentSubtypeId,
    };
  }

  private get scopeState() {
    if (!this.scope) return undefined;
    return this.tendersResponses.uploadStates()[this.tendersResponses.fileKey(this.projectId ?? 'new', this.scope)];
  }

  get fileRows(): ResponseProjectUploadFile[] {
    return this.scopeState?.files.filter(f => !f.hidden) ?? [];
  }

  get uploadBatchCompleted(): number {
    return this.scopeState?.uploadCompleted ?? 0;
  }

  get uploadBatchTotal(): number {
    return this.scopeState?.uploadTotal ?? 0;
  }

  get categorizationCompleted(): number {
    return this.scopeState?.categorizationCompleted ?? 0;
  }

  get categorizationTotal(): number {
    return this.scopeState?.categorizationTotal ?? 0;
  }

  get processingStep(): DocumentProcessingStepEnum.UPLOADING | DocumentProcessingStepEnum.CATEGORIZING | null {
    if (this.fileRows.some(f => f.processingStep === DocumentProcessingStepEnum.CATEGORIZING)) {
      return DocumentProcessingStepEnum.CATEGORIZING;
    }
    if (
      this.fileRows.some(
        f =>
          f.processingStep === DocumentProcessingStepEnum.UPLOADING ||
          f.processingStep === DocumentProcessingStepEnum.UPLOADED ||
          f.processingStep === DocumentProcessingStepEnum.EXTRACTING
      )
    ) {
      return DocumentProcessingStepEnum.UPLOADING;
    }
    return null;
  }

  get continueSubmitted(): boolean {
    return this.fileRows.length > 0 && !this.isImportingFilesPhase;
  }

  get batchProgressPercent(): number {
    if (!this.uploadBatchTotal) return 0;
    return Math.min(100, (this.uploadBatchCompleted / this.uploadBatchTotal) * 100);
  }

  get isImportingFilesPhase(): boolean {
    return this.fileRows.some(f => f.processingStep === DocumentProcessingStepEnum.IMPORTING);
  }

  get uncategorizedCount(): number {
    return this.fileRows.filter(f => f.processingStep === DocumentProcessingStepEnum.UNCATEGORIZED).length;
  }

  get addedCount(): number {
    return this.fileRows.length;
  }

  // Display order: uncategorized documents first so the user can act on them,
  // then alphabetical (same comparator as addFilesToState) within each group.
  get sortedFileRows(): ResponseProjectUploadFile[] {
    return [...this.fileRows].sort((a, b) => {
      const aUncat = a.processingStep === DocumentProcessingStepEnum.UNCATEGORIZED;
      const bUncat = b.processingStep === DocumentProcessingStepEnum.UNCATEGORIZED;
      if (aUncat !== bUncat) return aUncat ? -1 : 1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }

  get newFiles(): ResponseProjectUploadFile[] {
    return this.fileRows.filter(
      f => f.processingStep === DocumentProcessingStepEnum.IMPORTING || f.processingStep === DocumentProcessingStepEnum.UPLOADING
    );
  }

  onCancelConfigAdd(): void {
    const effectiveId = this.projectId ?? 'new';
    const toKeep = this.fileRows.filter(f => f.processingStep !== DocumentProcessingStepEnum.IMPORTING);
    this.tendersResponses.setFiles(effectiveId, this.scope, toKeep);
    this.isAddingToExisting = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] && this.projectId) {
      const isIntendedTarget = this.pendingProcess || this.projectId === this.tendersResponses.newProjectId();
      if (!isIntendedTarget) return;

      const tempKey = this.tendersResponses.fileKey('new', this.scope);
      const pending = this.tendersResponses.uploadStates()[tempKey]?.files ?? [];
      const hadPendingFiles = pending.length > 0;
      if (hadPendingFiles) {
        this.tendersResponses.setFiles('new', this.scope, []);
        this.tendersResponses.setFiles(this.projectId, this.scope, pending);
      }
      if (this.pendingProcess || hadPendingFiles) {
        this.pendingProcess = false;
        void this.processFiles();
      }
    }
  }

  ngAfterViewChecked(): void {
    if (this.scrollToTopPending && this.listContainerRef?.nativeElement) {
      this.scrollToTopPending = false;
      this.listContainerRef.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (this.scrollToFileIdPending && this.listContainerRef?.nativeElement) {
      const id = this.scrollToFileIdPending;
      this.scrollToFileIdPending = null;
      const el = this.listContainerRef.nativeElement.querySelector(`[data-file-id="${id}"]`) as HTMLElement | null;
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  ngOnDestroy(): void {
    if (this.projectId && this.tendersResponses.hasActiveProcessing(this.projectId, this.scope)) {
      this.activeWsConn = null;
      return;
    }
    this.unsubscribeFromWs();
    this.activeWsConn?.disconnect();
    this.activeWsConn = null;
  }

  togglePanelExpanded(value?: boolean): void {
    const next = value !== undefined ? value : !this.panelExpanded;
    if (!next && !this.continueSubmitted && !this.configMode) return;
    this.panelExpanded = next;
  }

  selectSubtype(file: ResponseProjectUploadFile, option: ResponseDocumentSubtype): void {
    const effectiveId = this.projectId ?? 'new';
    // Manual categorization: mark CATEGORIZED so the badge/banner/sort update without a page refresh.
    this.tendersResponses.patchFile(effectiveId, this.scope, file.uid, {
      subtype: option.displayedName,
      subtypeId: option.id,
      processingStep: DocumentProcessingStepEnum.CATEGORIZED,
    });
    if (file.documentId) {
      this.apiDocuments.updateDocument({ id: file.documentId, response_document_subtype_id: option.id }).subscribe({
        error: (err: { status?: number }) => {
          if (err.status === 404) {
            const effectiveId = this.projectId ?? 'new';
            this.tendersResponses.removeFile(effectiveId, this.scope, file.uid);
            this.documentNotFound.emit();
          }
        },
      });
    }
  }

  selectCustomSubtype(file: ResponseProjectUploadFile, customName: string): void {
    const autreOption = this.subtypeOptions.find(opt => opt.name === SUBTYPE_OTHER_NAME);
    const effectiveId = this.projectId ?? 'new';
    // Manual categorization: mark CATEGORIZED so the badge/banner/sort update without a page refresh.
    this.tendersResponses.patchFile(effectiveId, this.scope, file.uid, {
      subtype: this.SUBTYPE_OTHER,
      subtypeId: autreOption?.id,
      customName,
      processingStep: DocumentProcessingStepEnum.CATEGORIZED,
    });
    if (file.documentId) {
      this.apiDocuments
        .updateDocument({
          id: file.documentId,
          custom_name: customName,
          ...(autreOption && { response_document_subtype_id: autreOption.id }),
        })
        .subscribe({
          error: (err: { status?: number }) => {
            if (err.status === 404) {
              const effectiveId = this.projectId ?? 'new';
              this.tendersResponses.removeFile(effectiveId, this.scope, file.uid);
              this.documentNotFound.emit();
            }
          },
        });
    }
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) this.handleNewFiles(Array.from(input.files));
    input.value = '';
  }

  triggerFileInput(fileInput: HTMLInputElement): void {
    if (this.projectId) {
      this.apiProjects.getById(this.projectId).subscribe({
        next: () => {
          fileInput.click();
        },
        error: (err: { status?: number }) => {
          if (err.status === 404) this.projectNotFound.emit();
        },
      });
    } else {
      fileInput.click();
    }
  }

  // Files coming from the shared DocumentImportZone brick: validate the project
  // still exists (same guard as the inline dropzone) before staging them.
  onZoneFilesSelected(files: File[]): void {
    if (this.projectId) {
      this.apiProjects.getById(this.projectId).subscribe({
        next: () => this.handleNewFiles(files),
        error: (err: { status?: number }) => {
          if (err.status === 404) this.projectNotFound.emit();
        },
      });
    } else {
      this.handleNewFiles(files);
    }
  }

  onZoneFilesRejected(rejected: { name: string; reason: string }[]): void {
    for (const entry of rejected) this.ignoredFiles.push(entry);
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter++;
    this.isDragging = true;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter--;
    if (this.dragCounter <= 0) {
      this.dragCounter = 0;
      this.isDragging = false;
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter = 0;
    this.isDragging = false;

    const items = event.dataTransfer?.items;
    if (!items) return;

    const acceptedExts = this.accept.split(',');

    const isAccepted = (name: string) => acceptedExts.some(ext => name.toLowerCase().endsWith(ext));

    const ignored = (name: string) =>
      this.ignoredFiles.push({
        name,
        reason: 'tenders.responses.documents.ignored-reason-unsupported-file-type',
      });

    const files: File[] = [];

    for (const item of Array.from(items)) {
      const entry = (item as any).webkitGetAsEntry?.();
      if (!entry) continue;

      if (entry.isDirectory) {
        ignored(entry.name);
        continue;
      }

      if (entry.isFile) {
        const file = item.getAsFile();

        if (!file) continue;

        if (!isAccepted(file.name)) {
          ignored(file.name);
          continue;
        }

        files.push(file);
      }
    }

    if (files.length) {
      if (this.projectId) {
        this.apiProjects.getById(this.projectId).subscribe({
          next: () => {
            this.handleNewFiles(files);
          },
          error: (err: { status?: number }) => {
            if (err.status === 404) this.projectNotFound.emit();
          },
        });
      } else {
        this.handleNewFiles(files);
      }
    }
  }
  private handleNewFiles(newFiles: File[]): void {
    this.addFilesToState(newFiles);
  }

  get dedupEnabled(): boolean {
    return this.scope === SCOPE_DCE || this.scope === SCOPE_INTERNAL_DOC;
  }

  private addDuplicate(name: string, existingId: number | undefined, existingSubtypeId: number | undefined, file: File | undefined): void {
    if (this.duplicates.some(d => d.name === name)) return;
    this.duplicates = [...this.duplicates, { uid: `dup-${++this.duplicateCounter}`, name, existingId, existingSubtypeId, file }];
  }

  private addConflict(conflictKey: string, name: string, existingId: number | undefined, existingSubtypeId: number | undefined): void {
    if (!this.conflicts.tryAdd(conflictKey, name, existingId, existingSubtypeId)) return;
    this.duplicates = [...this.duplicates, { uid: `dup-${++this.duplicateCounter}`, name, existingId, existingSubtypeId, conflictKey }];
    if (this.projectId) this.tendersResponses.decrementCategorizationTotal(this.projectId, this.scope);
  }

  private subtypeIdOf(file: ResponseProjectUploadFile | undefined): number | undefined {
    return file ? (findUploadSubtype(file, this.subtypeOptions)?.id ?? file.subtypeId) : undefined;
  }

  private get hasPendingConflicts(): boolean {
    return this.conflicts.hasPending;
  }

  private dismissDuplicate(item: DuplicateItem): void {
    setTimeout(() => {
      item.phase = 'collapsing';
      this.duplicates = [...this.duplicates];
    }, 1500);
    setTimeout(() => {
      this.duplicates = this.duplicates.filter(d => d.uid !== item.uid);
    }, 1800);
  }

  private removeConflict(conflictKey: string): void {
    this.conflicts.delete(conflictKey);
    this.maybeCloseWsConn();
  }

  private maybeCloseWsConn(): void {
    if (this.wsProcessingComplete && !this.hasPendingConflicts && this.activeWsConn) {
      this.activeWsConn.disconnect();
      this.activeWsConn = null;
    }
  }

  private presentPieceByName(name: string): ResponseProjectUploadFile | undefined {
    return this.fileRows.find(
      f =>
        f.name === name &&
        f.documentId != null &&
        (f.processingStep === DocumentProcessingStepEnum.CATEGORIZED ||
          f.processingStep === DocumentProcessingStepEnum.UNCATEGORIZED ||
          f.processingStep === DocumentProcessingStepEnum.UPLOADED)
    );
  }

  onDuplicateResolve(event: { item: DuplicateItem; resolution: DuplicateResolution }): void {
    const { item, resolution } = event;
    if (item.resolution) return;
    item.resolution = resolution;
    item.phase = 'leaving';
    this.duplicates = [...this.duplicates];

    // Server-side conflict
    if (item.conflictKey) {
      this.conflicts.markResolution(item.conflictKey, resolution);
      this.activeWsConn?.sendConflictResolution({
        action: resolution,
        key: item.conflictKey,
        document_id: resolution === 'replace' ? item.existingId : undefined,
        document_subtype_id: resolution === 'replace' ? item.existingSubtypeId : undefined,
      });
    } else if (resolution === 'replace' && item.file) {
      this.replaceWithClientFile(item);
    }

    this.dismissDuplicate(item);
  }

  /**
   * "Replace" for a direct (non-zip) collision detected at import.
   */
  private replaceWithClientFile(item: DuplicateItem): void {
    if (!item.file) return;
    if (item.existingId == null) {
      this.ignoredFiles.push({ name: item.name, reason: 'tenders.responses.documents.ignored-reason-replace-failed' });
      return;
    }
    const existing = this.fileRows.find(f => f.documentId === item.existingId);
    const result = this.projectImport.queueReplace({
      projectId: this.projectId,
      scope: this.scope,
      ctx: { ...this.importContext, defaultSubtypeId: item.existingSubtypeId ?? this.subtypeIdOf(existing) },
      uid: `tmp-${++this.tempFileCounter}`,
      file: item.file,
      replaceId: item.existingId,
      subtype: existing?.subtype ?? this.subtypeOptions.find(o => o.id === item.existingSubtypeId)?.displayedName,
      customName: existing?.customName,
      existingUid: existing?.uid,
      onError: name => this.ignoredFiles.push({ name, reason: 'tenders.responses.documents.ignored-reason-replace-failed' }),
    });
    this.uploadStateChange.emit({ files: [...this.fileRows], scenario: null });
    if (result === 'pending') this.pendingProcess = true;
  }

  private addFilesToState(newFiles: File[]): void {
    const effectiveId = this.projectId ?? 'new';
    this.isAddingToExisting = this.fileRows.some(
      f => f.processingStep === DocumentProcessingStepEnum.CATEGORIZED || f.processingStep === DocumentProcessingStepEnum.UNCATEGORIZED
    );
    const batchNames = new Set<string>();
    let changed = false;

    for (const file of newFiles) {
      if (batchNames.has(file.name)) continue;

      const sameName = this.fileRows.find(f => f.name === file.name);
      if (!sameName && this.fileRows.length >= MAX_DOCUMENTS_PER_SCOPE) continue;
      const isArchive = isArchiveFile(file.name);
      const maxSize = isArchive ? MAX_ARCHIVE_SIZE_BYTES : MAX_FILE_SIZE_BYTES;
      if (file.size > maxSize) {
        this.ignoredFiles.push({
          name: file.name,
          reason: isArchive
            ? 'tenders.responses.documents.ignored-reason-too-large-archive'
            : 'tenders.responses.documents.ignored-reason-too-large',
        });
        continue;
      }
      const pendingSameName = this.fileRows.find(f => f.name === file.name && f.documentId == null);
      if (pendingSameName) this.tendersResponses.removeFile(effectiveId, this.scope, pendingSameName.uid);
      this.tendersResponses.appendFile(effectiveId, this.scope, {
        uid: `tmp-${++this.tempFileCounter}`,
        name: file.name,
        size: file.size,
        sourceFile: file,
        processingStep: DocumentProcessingStepEnum.IMPORTING,
      });
      batchNames.add(file.name);
      changed = true;
    }
    if (!changed) return;

    this.tendersResponses.setFiles(
      effectiveId,
      this.scope,
      [...this.fileRows].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
    );
    this.uploadStateChange.emit({ files: [...this.fileRows], scenario: null });
  }

  onDelete(file: ResponseProjectUploadFile): void {
    const effectiveId = this.projectId ?? 'new';
    this.tendersResponses.removeFile(effectiveId, this.scope, file.uid);
    this.uploadStateChange.emit({ files: [...this.fileRows], scenario: null });
    if (file.documentId) {
      this.apiDocuments.deleteDocument(file.documentId).subscribe({
        error: (err: { status?: number }) => {
          if (err.status === 404) this.documentNotFound.emit();
        },
      });
    }
  }

  isZipFile(file: ResponseProjectUploadFile): boolean {
    return isArchiveFile(file.name);
  }

  onContinueClick(): void {
    this.continueClicked.emit();
    if (this.projectId) {
      void this.processFiles();
    } else {
      this.pendingProcess = true;
    }
  }

  async processFiles(): Promise<void> {
    const toUpload = this.fileRows.filter(f => f.sourceFile instanceof File);
    if (!toUpload.length || !this.projectId) return;

    this.unsubscribeFromWs();
    this.wsProcessingComplete = false;

    try {
      const result = await this.projectImport.run({
        projectId: this.projectId,
        scope: this.scope,
        ctx: this.importContext,
        files: toUpload,
        defaultSubtypeId: this.defaultDocumentSubtypeId,
        conflicts: this.conflicts,
        dedupEnabled: this.dedupEnabled,
        getFiles: () => this.fileRows,
        presentPieceByName: name => this.presentPieceByName(name),
        subtypeIdOf: file => this.subtypeIdOf(file),
        onConflict: (key, name, existingId, existingSubtypeId) => this.addConflict(key, name, existingId, existingSubtypeId),
        onConflictExpired: key => this.onConflictExpired(key),
        removeConflict: key => this.removeConflict(key),
        maybeCloseWs: () => this.maybeCloseWsConn(),
        ignoreFile: (name, reason) => this.ignoredFiles.push({ name, reason }),
        onUploadError: (file, stage, reason) => this.handleImportError(file, stage, reason),
        onCategorizing: () => {
          if (!this.configMode) this.togglePanelExpanded(false);
          this.uploadStateChange.emit({ files: [...this.fileRows], scenario: ResponseProjetAiMessageScenario.CATEGORIZING });
        },
        onCategorized: () => {
          this.uploadStateChange.emit({ files: [...this.fileRows], scenario: ResponseProjetAiMessageScenario.WORKING });
        },
        onProcessingComplete: () => {
          this.wsProcessingComplete = true;
          if (this.uncategorizedCount > 0) this.scrollToTopPending = true;
          this.maybeCloseWsConn();
        },
        extraSubscriptions: wsConn => [
          wsConn.messages$.pipe(filter(msg => msg.status === 'renamed project')).subscribe(msg => {
            if (msg.status === 'renamed project') this.projectNameChange.emit(msg.project_name);
          }),
        ],
      });
      if (result.wsConn) {
        this.activeWsConn = result.wsConn;
        this.wsSubscriptions.push(...result.subscriptions);
      }
      if (this.fileRows.length > 0) {
        this.scrollToFileIdPending = toUpload.length === 1 ? toUpload[0].uid : this.fileRows.at(-1)!.uid;
      }
    } finally {
      this.uploadStateChange.emit({ files: [...this.fileRows], scenario: ResponseProjetAiMessageScenario.READY });
    }
  }

  private onConflictExpired(conflictKey: string): void {
    const name = this.conflicts.get(conflictKey)?.name ?? this.duplicates.find(d => d.conflictKey === conflictKey)?.name;
    if (name) this.ignoredFiles.push({ name, reason: 'tenders.responses.documents.ignored-reason-conflict-expired' });
    this.duplicates = this.duplicates.filter(d => d.conflictKey !== conflictKey || d.resolution);
    this.removeConflict(conflictKey);
  }

  getSubTypeDisplayName(file: ResponseProjectUploadFile): string {
    return uploadSubtypeDisplayName(file, this.subtypeOptions);
  }

  subtypeNameOf(file: ResponseProjectUploadFile): string | null {
    return uploadSubtypeSlug(file, this.subtypeOptions);
  }

  private unsubscribeFromWs(): void {
    this.wsSubscriptions.forEach(sub => sub.unsubscribe());
    this.wsSubscriptions = [];
  }

  private handleImportError(
    file: ResponseProjectUploadFile,
    stage: 'create' | 'upload',
    reason: 'not-found' | 'already-exists' | 'upload-error'
  ): void {
    if (this.projectId) {
      const isRegular = !this.isZipFile(file);
      if (stage === 'upload' || isRegular) this.tendersResponses.incrementUploadCompleted(this.projectId, this.scope);
      // Decrement categorization total if the file was not created.
      if (stage === 'create' && isRegular) this.tendersResponses.decrementCategorizationTotal(this.projectId, this.scope);
      this.tendersResponses.removeFile(this.projectId, this.scope, file.uid);
    }
    if (reason === 'not-found') {
      this.projectNotFound.emit();
    } else if (reason === 'already-exists') {
      const incoming = file.sourceFile instanceof File ? file.sourceFile : undefined;
      const localPiece = this.fileRows.find(f => f.name === file.name && f.documentId != null);
      if (localPiece || !this.projectId) {
        this.addDuplicate(file.name, localPiece?.documentId, this.subtypeIdOf(localPiece), incoming);
      } else {
        // Check for server-side document with the same name.
        this.apiDocuments.findByNameInProject(file.name, this.projectId).subscribe({
          next: res => {
            const scopeSubtypeIds = new Set(this.subtypeOptions.map(o => o.id));
            const named = res.data.filter(d => d.name === file.name);
            const match = named.find(d => scopeSubtypeIds.has(d.responseDocumentSubtypeId)) ?? named[0];
            this.addDuplicate(file.name, match ? Number(match.id) : undefined, match?.responseDocumentSubtypeId, incoming);
          },
          error: () => this.addDuplicate(file.name, undefined, undefined, incoming),
        });
      }
    } else {
      this.ignoredFiles.push({ name: file.name, reason: 'tenders.responses.documents.ignored-reason-upload-error' });
    }
  }
}
